/**
 * App Component
 *
 * The root component of the application that sets up the navigation container
 * and includes the bottom navigation component (BottomNavComponent) to render
 * the tab navigation throughout the app.
 *
 * Main Components:
 * 1. BottomNavComponent: Handles the bottom navigation with several tabs.
 * 2. NavigationContainer: Wraps the BottomNavComponent and manages navigation state.
 * 3. View Container: A wrapper to apply background color and handle layout.
 *
 * Dependencies:
 *  React Navigation for handling navigation.
 *  BottomNavComponent for the tabbased navigation system.
 *  COLORS from constants for theming and styling.
 */
import React, { useEffect, useRef } from "react";
import AppSettings from "./storage/appSettings";
import BottomNavComponent from "./components/BottomNavComponent/BottomNavComponent";
import { NavigationContainer } from "@react-navigation/native";
import { PaperProvider } from "react-native-paper";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { View, StyleSheet, Platform } from "react-native";
import { getApiKey, subscribeToWebPushNotifications, getPushNotificationApiKey } from "./utils/api";
import { COLORS } from "./constants";
import InstallPromptWeb from "./components/InstallPromptWeb/InstallPromptWeb";
import { logAnalyticsEvent, app } from "./utils/firebase";
import { useInitPushNotifications } from "./sub/utils/pushNotifications";

// Disable console logs in production mode
if (process.env.NODE_ENV === "production") {
  console.info("Production mode: Disabling console logs");
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
}

const platform = Platform.OS === "web" ? "web" : "native";

const linking = {
  prefixes: [
    "http://localhost:3000/#/",
    "vbgo://",
    "https://vbgo.virginiabeach.gov",
    "https://vbgotest.virginiabeach.gov",
    "https://vbgodev.virginiabeach.gov",
  ],
  config: {
    screens: {
      Parking: {
        screens: {
          ParkingListView: "parking",
          ParkingMapView: "parking/map",
          ParkingDetailsView: "parking/details",
        },
      },
      Attractions: {
        screens: {
          AttractionsListView: "attractions",
          AttractionsMapView: "attractions/map",
          AttractionsDetailsView: "attractions/details",
        },
      },
      Bathrooms: {
        screens: {
          BathroomListView: "bathrooms",
          BathroomMapView: "bathrooms/map",
          BathroomDetailsView: "bathrooms/details",
        },
      },
      Settings: "settings",
      Events: {
        screens: {
          ListView: {
            path: "events",
            parse: {
              // Decode pipe-separated string back to array: "Aquarium|Parks & Recreation" → ["Aquarium", "Parks & Recreation"]
              category: (val) => {
                if (!val) return [];
                return String(val).split("|").map(decodeURIComponent).filter(Boolean);
              },
            },
            stringify: {
              // Join array with pipe separator — React Navigation handles URL encoding
              category: (val) => {
                if (!val || (Array.isArray(val) && val.length === 0)) return undefined;
                const arr = Array.isArray(val) ? val : [val];
                return arr.join("|");
              },
            },
          },
          FiltersView: "events/filters",
          DetailsView: {
            path: "events/details",
            parse: {
              eventKey: (eventKey) => decodeURIComponent(eventKey),
            },
          },
        },
      },
    },
  },
  ...(Platform.OS === "web" && {
    getInitialURL: async () => window.location.href,
  }),
};
/**
 * App Component
 *
 * This is the entry point for the application. It renders the NavigationContainer
 * and wraps the BottomNavComponent inside a View container. The NavigationContainer
 * manages the app's navigation flow, while the BottomNavComponent handles the bottom tabs.
 */
const App = () => {
  const setNotificationSubscription = AppSettings((state) => state.setNotificationSubscription);
  const setNotificationToken = AppSettings((state) => state.setNotificationToken);
  const notificationToken = AppSettings((state) => state.notificationToken);
  const setGoogleApiKey = AppSettings((state) => state.setGoogleApiKey);
  const firebaseVapidKey = AppSettings((state) => state.firebaseVapidKey);
  const setFirebaseVapidKey = AppSettings((state) => state.setFirebaseVapidKey);

  const unsubscribeRef = useRef(null);

  logAnalyticsEvent("screen_view", { name: "VBGo-App" });

  /**
   * The objects needed by the push notification init submodule function
   * Web and native require different objects
   */
  const firebaseInit =
    platform === "web"
      ? {
        //TODO:Commented out to fix error bug #38212 fixed in PR #7102
          // app,
          // getApiKey: getPushNotificationApiKey,
          // setFirebaseVapidKey,
          // firebaseVapidKey,
          // setNotificationToken,
          // notificationToken,
          // subscribeToWebPushNotifications,
          // setNotificationSubscription,
        }
      : {
          unsubscribeRef,
          setNotificationToken,
          notificationToken,
        };

  // Hide notification subscription and alert message on web refresh
  useEffect(() => {
    if (platform === "web") {
      setNotificationSubscription({ show: false, type: "" });
      // setShowAlert(false);
    }
  }, [setNotificationSubscription /**setShowAlert**/]); // Only run once on mount

  useInitPushNotifications(platform, "vbgo", firebaseInit);

  // Fetch and store the Google Maps API key on load.
  // Runs on every mount so the key refreshes each time the page is refreshed.
  useEffect(() => {
    (async () => {
      try {
        const apiKeyResponse = await getApiKey("googleMaps");
        setGoogleApiKey(apiKeyResponse.data);
      } catch (error) {
        console.error("❌ Error fetching API key:", error);
      }
    })();
  }, [setGoogleApiKey]);

  return (
    <SafeAreaProvider>
      <View style={styles.container}>
        <PaperProvider>
          <NavigationContainer linking={linking}>
            <BottomNavComponent />
          </NavigationContainer>
        </PaperProvider>
        {Platform.OS === "web" && <InstallPromptWeb />}
      </View>
    </SafeAreaProvider>
  );
};

export default App;

/**
 * Styles for the App component
 *
 * container: Defines the overall layout of the app, ensures the app takes up the
 *            entire screen, and sets the background color from the constants file.
 */
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.vbgoBlue,
  },
});
